import pick from 'lodash/pick';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { getRoomTypes } from '../../../../../api/rooms/rooms';
import { SelectOptionsT } from '../../../../../components/SelectDropdown/SelectDropdown';
import { useLocale } from '../../../../../context/LocaleContext';
import { CalculatorTypeE } from '../../../../../declarations/enums/common';
import { RoutesPathE } from '../../../../../declarations/enums/routes';
import useReactRouter from '../../../../../hooks/useReactRouter';

const useTransportationAndHotelOffset = (customerId: string, customerName: string) => {
  const { navigateTo } = useReactRouter();
  const { selectedLocale } = useLocale();
  const DEFAULT_FORM_DATA = useMemo(
    () => ({
      roomType: {} as SelectOptionsT,
      hotel: { value: customerId, label: customerName },
      arrivalDate: new Date(),
      numberOfNights: 1,
      numberOfRooms: 1,
      totalNumberOfGuests: 1,
    }),
    []
  );

  const [rooms, setRooms] = useState<SelectOptionsT[]>([] as SelectOptionsT[]);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_DATA);

  const onFormFieldChange = (
    name: keyof typeof DEFAULT_FORM_DATA,
    val: typeof DEFAULT_FORM_DATA[keyof typeof DEFAULT_FORM_DATA]
  ) => {
    setFormValues({ ...formValues, [name]: val });
  };

  const queryObj = useMemo(() => {
    return {
      hotel: {
        ...pick(formValues, [
          'arrivalDate',
          'numberOfNights',
          'numberOfRooms',
          'totalNumberOfGuests',
        ]),
        roomTypeId: formValues.roomType?.value || '',
      },
      calculate: CalculatorTypeE.HOTEL,
    };
  }, [formValues]);

  const onClickHotel = () => {
    navigateTo(
      RoutesPathE.CARBON_OFFSETS_PAGE,
      { ...queryObj, calculate: CalculatorTypeE.HOTEL },
      true
    );
  };

  const onClickHotelAndTransport = () => {
    navigateTo(RoutesPathE.TRANSPORTATION_PAGE, {
      ...queryObj,
      calculate: CalculatorTypeE.HOTEL_AND_TRANSPORT,
    });
  };

  const { data: roomTypes } = useQuery(['rooms'], () => getRoomTypes(customerId), {
    enabled: !!customerId,
    select: data => data.data,
    onSuccess(data) {
      const parsedRooms = data.map(({ id, vanityName }) => ({
        value: id,
        label: vanityName[selectedLocale],
      }));

      onFormFieldChange('roomType', parsedRooms[0]);
    },
  });

  useEffect(() => {
    if (roomTypes?.length) {
      const translatedRoomTypes = roomTypes?.map(({ id, vanityName }) => ({
        value: id,
        label: vanityName[selectedLocale],
      }));
      const translatedSelectedRoomType = translatedRoomTypes.find(
        type => type.value === formValues.roomType.value
      );
      setRooms(translatedRoomTypes);
      setFormValues({ ...formValues, roomType: translatedSelectedRoomType as SelectOptionsT });
    }
  }, [roomTypes, selectedLocale]);

  return {
    rooms,
    formValues,
    onFormFieldChange,
    onClickHotel,
    onClickHotelAndTransport,
  };
};

export default useTransportationAndHotelOffset;
